import { useState, useEffect, useRef, useCallback } from 'react';
import { Globe, CheckCircle, AlertCircle, Copy, ExternalLink, RefreshCw, Save, Shield, Loader2, Trash2 } from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { fetchDomain, saveDomain, verifyDomain, checkDomainSSL, removeDomain } from '@/lib/api';
import type { Tenant } from '@/types';

export function DomainPage({ tenant }: { tenant: Tenant }) {
  const [customDomain, setCustomDomain] = useState(tenant.custom_domain || '');
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkingSSL, setCheckingSSL] = useState(false);

  // Domain record from DB
  const [domainRecord, setDomainRecord] = useState<{
    id: string; domain: string; verification_token: string;
    verified_at: string | null; ssl_status: string; cloudflare_hostname_id?: string;
  } | null>(null);

  const [verifyResult, setVerifyResult] = useState<{
    verified: boolean; message?: string;
  } | null>(null);

  const [sslMessage, setSslMessage] = useState<string | null>(null);

  // Remove domain state
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  // SSL polling interval ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load existing domain record on mount
  useEffect(() => {
    setLoading(true);
    fetchDomain(tenant.id)
      .then(record => {
        if (record) {
          setDomainRecord(record);
          setCustomDomain(record.domain);
        }
      })
      .catch(err => console.error('Failed to load domain:', err))
      .finally(() => setLoading(false));
  }, [tenant.id]);

  // Auto-poll SSL status when domain is verified but SSL not yet active
  const pollSSLStatus = useCallback(async () => {
    if (!domainRecord?.verified_at || !domainRecord.cloudflare_hostname_id) return;
    if (domainRecord.ssl_status === 'active') return;

    try {
      const result = await checkDomainSSL(tenant.id, domainRecord.domain);
      setSslMessage(result.message || null);
      setDomainRecord(prev => prev ? { ...prev, ssl_status: result.ssl_status } : prev);

      // Stop polling once SSL is active
      if (result.ssl_status === 'active' && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      console.error('SSL status check failed:', err);
    }
  }, [domainRecord, tenant.id]);

  useEffect(() => {
    // Start polling if domain is verified but SSL isn't active yet
    if (domainRecord?.verified_at && domainRecord.ssl_status !== 'active' && domainRecord.cloudflare_hostname_id) {
      // Check immediately
      pollSSLStatus();
      // Then poll every 30 seconds
      pollRef.current = setInterval(pollSSLStatus, 30000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [domainRecord?.verified_at, domainRecord?.ssl_status, domainRecord?.cloudflare_hostname_id, pollSSLStatus]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  const handleSaveDomain = async () => {
    if (!customDomain.trim()) return;
    setSaving(true);
    setVerifyResult(null);
    setSslMessage(null);
    try {
      const record = await saveDomain(tenant.id, customDomain.trim());
      setDomainRecord({ ...record, verified_at: null, ssl_status: 'pending' });
    } catch (err: any) {
      console.error('Failed to save domain:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (!domainRecord) return;
    setVerifying(true);
    setVerifyResult(null);
    setSslMessage(null);
    try {
      const result = await verifyDomain(tenant.id, domainRecord.domain);
      setVerifyResult(result);
      if (result.verified) {
        // Refresh the domain record to get the updated cloudflare_hostname_id
        const refreshed = await fetchDomain(tenant.id);
        if (refreshed) {
          setDomainRecord(refreshed);
        } else {
          setDomainRecord(prev => prev ? { ...prev, verified_at: new Date().toISOString(), ssl_status: 'provisioning' } : prev);
        }
      }
    } catch (err: any) {
      setVerifyResult({ verified: false, message: err.message || 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckSSL = async () => {
    if (!domainRecord) return;
    setCheckingSSL(true);
    try {
      const result = await checkDomainSSL(tenant.id, domainRecord.domain);
      setSslMessage(result.message || null);
      setDomainRecord(prev => prev ? { ...prev, ssl_status: result.ssl_status } : prev);
    } catch (err: any) {
      setSslMessage(err.message || 'Failed to check SSL status');
    } finally {
      setCheckingSSL(false);
    }
  };

  const handleRemoveDomain = async () => {
    setRemoving(true);
    setRemoveError(null);
    try {
      await removeDomain(tenant.id);
      // Reset all domain state
      setDomainRecord(null);
      setCustomDomain('');
      setVerifyResult(null);
      setSslMessage(null);
      setShowRemoveConfirm(false);
      // Stop SSL polling
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  const verificationToken = domainRecord?.verification_token || tenant.id.slice(0, 16);
  const isVerified = !!domainRecord?.verified_at;
  const domainChanged = customDomain.trim() !== (domainRecord?.domain || '');
  const sslIsActive = domainRecord?.ssl_status === 'active';
  const sslIsProvisioning = domainRecord?.ssl_status === 'provisioning';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-surface-900">Domain</h1>
        <p className="text-sm text-surface-500 mt-1">Configure your subdomain and custom domain</p>
      </div>

      {/* Current subdomain */}
      <div className="glass-panel p-5">
        <h3 className="text-sm font-semibold text-surface-900 mb-1">Subdomain</h3>
        <p className="text-xs text-surface-500 mb-4">Your default platform URL. This is always active.</p>

        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center gap-2 px-4 py-2.5 bg-surface-50 border border-surface-300 rounded-xl">
            <Globe size={16} className="text-brand-500 shrink-0" />
            <span className="text-sm font-mono text-surface-900">{tenant.subdomain}</span>
          </div>
          <button onClick={() => copyToClipboard(`https://${tenant.subdomain}`)}
            className="btn-secondary py-2.5"><Copy size={14} /> Copy</button>
          <a href={`https://${tenant.subdomain}`} target="_blank" rel="noopener noreferrer"
            className="btn-secondary py-2.5"><ExternalLink size={14} /></a>
        </div>

        <div className="flex items-center gap-2 mt-3">
          <CheckCircle size={14} className="text-emerald-500" />
          <span className="text-xs text-emerald-400">Active and serving traffic</span>
        </div>
      </div>

      {/* Custom domain */}
      <div className="glass-panel p-5">
        <h3 className="text-sm font-semibold text-surface-900 mb-1">Custom Domain</h3>
        <p className="text-xs text-surface-500 mb-4">Use your own domain instead of the subdomain. Requires DNS configuration.</p>

        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner size="sm" />
            <span className="ml-2 text-xs text-surface-500">Loading domain...</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3">
              <input type="text" className="input-field flex-1" placeholder="trips.yourcompany.com"
                value={customDomain} onChange={e => setCustomDomain(e.target.value)} />
              {domainChanged || !domainRecord ? (
                <button onClick={handleSaveDomain} disabled={!customDomain.trim() || saving} className="btn-primary text-sm">
                  {saving ? <Spinner size="sm" /> : <Save size={14} />}
                  Save Domain
                </button>
              ) : (
                <button onClick={handleVerify} disabled={verifying} className="btn-primary text-sm">
                  {verifying ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                  Verify DNS
                </button>
              )}
            </div>

            {domainRecord && (
              <div className="mt-5 space-y-4">
                {/* DNS instructions */}
                <div className="p-4 bg-surface-50 border border-surface-200 rounded-xl">
                  <h4 className="text-sm font-medium text-surface-900 mb-3">DNS Configuration</h4>
                  <p className="text-xs text-surface-500 mb-4">
                    Add these DNS records at your domain registrar to point <span className="font-mono text-surface-800">{domainRecord.domain}</span> to the platform:
                  </p>

                  <div className="space-y-3">
                    <DNSRecord type="CNAME" name={domainRecord.domain} value="proxy.discoverygem.com" onCopy={copyToClipboard}
                      note="Points your domain to the platform via Cloudflare" />
                    <DNSRecord type="TXT" name={`_verify.${domainRecord.domain}`} value={`unravel-verify=${verificationToken}`} onCopy={copyToClipboard}
                      note="Proves ownership of this domain" />
                  </div>

                  <div className="mt-3 p-2.5 banner-blue border rounded-lg">
                    <p className="text-xs text-blue-400">
                      <strong>Note:</strong> DNS changes can take up to 48 hours to propagate. The CNAME record must be in place
                      for Cloudflare to issue an SSL certificate for your custom domain.
                    </p>
                  </div>
                </div>

                {/* Verification result feedback */}
                {verifyResult && (
                  <div className={`p-3 rounded-lg border text-sm ${
                    verifyResult.verified
                      ? 'banner-emerald text-emerald-400'
                      : 'banner-amber text-amber-400'
                  }`}>
                    {verifyResult.verified
                      ? 'Domain verified successfully! SSL certificate is being provisioned via Cloudflare.'
                      : verifyResult.message || 'DNS records not found. Changes can take up to 48 hours to propagate.'}
                  </div>
                )}

                {/* Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isVerified ? (
                      <>
                        <CheckCircle size={14} className="text-emerald-500" />
                        <span className="text-xs text-emerald-400">
                          Domain verified{sslIsActive ? ' — SSL certificate active' : sslIsProvisioning ? ' — SSL provisioning via Cloudflare' : ''}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={14} className="text-amber-500" />
                        <span className="text-xs text-amber-400">DNS records not yet verified. Click "Verify DNS" after adding the records above.</span>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => setShowRemoveConfirm(true)}
                    className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 size={13} />
                    Remove Domain
                  </button>
                </div>

                {/* Remove domain confirmation */}
                {showRemoveConfirm && (
                  <div className="p-4 banner-red border rounded-xl">
                    <p className="text-sm text-red-400 mb-3">
                      Remove <span className="font-mono font-medium">{domainRecord.domain}</span>?
                      This will delete the Cloudflare SSL certificate, DNS configuration, and domain record.
                    </p>
                    {removeError && (
                      <p className="text-xs text-red-400 mb-3 p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">{removeError}</p>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleRemoveDomain}
                        disabled={removing}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
                      >
                        {removing ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                        {removing ? 'Removing…' : 'Yes, Remove Domain'}
                      </button>
                      <button
                        onClick={() => { setShowRemoveConfirm(false); setRemoveError(null); }}
                        disabled={removing}
                        className="px-3 py-1.5 text-xs text-surface-400 hover:text-surface-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* SSL Certificate section */}
      <div className="glass-panel p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-surface-900">SSL Certificate</h3>
          {isVerified && domainRecord?.cloudflare_hostname_id && !sslIsActive && (
            <button onClick={handleCheckSSL} disabled={checkingSSL}
              className="btn-secondary text-xs py-1.5 px-3">
              {checkingSSL ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              Check Status
            </button>
          )}
        </div>
        <p className="text-xs text-surface-500 mb-3">
          SSL certificates are automatically provisioned via Cloudflare for custom domains.
        </p>

        {!domainRecord || !isVerified ? (
          /* No custom domain or not verified yet */
          <div className="flex items-center gap-3 p-3 bg-surface-50 border border-surface-200 rounded-xl">
            <div className="p-2 rounded-lg bg-surface-100">
              <Shield size={16} className="text-surface-400" />
            </div>
            <div>
              <div className="text-sm text-surface-600">No custom domain configured</div>
              <div className="text-xs text-surface-500">Save and verify a custom domain above to provision an SSL certificate</div>
            </div>
          </div>
        ) : sslIsActive ? (
          /* SSL is active */
          <div className="flex items-center gap-3 p-3 banner-emerald border rounded-xl">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <Shield size={16} className="text-emerald-400" />
            </div>
            <div>
              <div className="text-sm font-medium text-emerald-400">SSL Certificate Active</div>
              <div className="text-xs text-emerald-400">
                Provisioned via Cloudflare for SaaS — auto-renews before expiry
              </div>
            </div>
          </div>
        ) : (
          /* SSL is provisioning */
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 banner-amber border rounded-xl">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Loader2 size={16} className="text-amber-400 animate-spin" />
              </div>
              <div>
                <div className="text-sm font-medium text-amber-400">SSL Certificate Provisioning</div>
                <div className="text-xs text-amber-400">
                  Cloudflare is issuing your SSL certificate. This usually takes a few minutes.
                </div>
              </div>
            </div>

            {sslMessage && (
              <div className="p-2.5 bg-surface-50 border border-surface-200 rounded-lg">
                <p className="text-xs text-surface-600">{sslMessage}</p>
              </div>
            )}

            <div className="p-2.5 banner-blue border rounded-lg">
              <p className="text-xs text-blue-400">
                <strong>Tip:</strong> Ensure your CNAME record (<span className="font-mono">{domainRecord.domain}</span> → <span className="font-mono">proxy.discoverygem.com</span>) is
                active. Cloudflare uses HTTP validation which requires the CNAME to be in place.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DNSRecord({ type, name, value, onCopy, note }: {
  type: string; name: string; value: string; onCopy: (t: string) => void; note?: string;
}) {
  return (
    <div className="p-3 bg-surface-0 border border-surface-200 rounded-lg">
      <div className="flex items-center gap-3">
        <span className="px-2 py-0.5 rounded text-xs font-bold bg-surface-200 text-surface-700 shrink-0">{type}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-surface-500">Name</div>
          <div className="text-sm font-mono text-surface-900 truncate">{name}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-surface-500">Value</div>
          <div className="text-sm font-mono text-surface-900 truncate">{value}</div>
        </div>
        <button onClick={() => onCopy(value)} className="p-1.5 rounded-lg hover:bg-surface-100 text-surface-400 shrink-0">
          <Copy size={14} />
        </button>
      </div>
      {note && <div className="text-[11px] text-surface-400 mt-1.5 ml-10">{note}</div>}
    </div>
  );
}
